define([
    '../Util/defaultValue',
    '../Util/defined',
    './OGCConnector',
    './WMSInformation'
], function (
    defaultValue,
    defined,
    OGCConnector,
    WMSInformation
) {
    'use strict'

    /**
     * WMS服务连接器，用于获取WMS服务元数据
     * @alias WMSConnector
     * @constructor
     * @extends OGCConnector
     */
    class WMSConnector extends OGCConnector {
		/**
         * @description 在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "WMSConnector"
        /**
         * 创建WMS服务连接器
         * @param {Object} props 配置选项
         * @param {String} props.url WMS服务地址
         * @param {String} [props.version='1.3.0'] WMS服务版本
         */
        constructor(props) {
            super({
                url: props.url,
                serviceType: 'WMS',
                version: defaultValue(props.version, '1.3.0')
            });
        }

        /**
         * 获取WMS服务能力信息
         * @param {Function} callback 回调函数，参数为(error, capabilities)
         */
        getCapabilities(callback) {
            const url = this._buildUrl({ REQUEST: 'GetCapabilities' });
            this._request(url, (error, response) => {
                if (error) {
                    if (defined(callback)) {
                        callback(error, null);
                    }
                    return;
                }

                try {
                    const xmlDoc = this._parseXML(response);
                    if (defined(callback)) {
                        callback(null, xmlDoc);
                    }
                } catch (e) {
                    if (defined(callback)) {
                        callback(new Error(`解析WMS能力文档失败: ${e.message}`), null);
                    }
                }
            });
        }

        /**
         * 从能力文档中提取图层信息
         * @param {Document} capabilities XML能力文档
         * @returns {Array<Object>} 图层信息数组
         * @protected
         * @override
         */
        _extractLayers(capabilities) {
            const layers = [];
            const layerNodes = capabilities.getElementsByTagName('Layer');

            for (let i = 0; i < layerNodes.length; i++) {
                const layerNode = layerNodes[i];
                const nameNode = layerNode.getElementsByTagName('Name')[0];

                // 只处理有Name节点的图层，作为可请求的图层
                if (!nameNode) {
                    continue;
                }

                const layerName = nameNode.textContent;
                const layerInfo = this._parseLayerInfo(layerNode);

                if (layerInfo) {
                    layers.push(layerInfo);
                }
            }

            return layers;
        }

        /**
         * 解析图层信息
         * @param {Element} layerNode 图层XML节点
         * @returns {WMSInformation} WMS图层信息对象
         * @private
         */
        _parseLayerInfo(layerNode) {
            // 创建WMSInformation对象
            const info = new WMSInformation({
                layerName: this._getElementText(layerNode, 'Name'),
                title: this._getElementText(layerNode, 'Title'),
                abstract: this._getElementText(layerNode, 'Abstract'),
                version: this.version
            });

            // 解析坐标参考系
            const crsNodes = layerNode.getElementsByTagName('CRS');
            if (crsNodes.length === 0) {
                // 尝试SRS (WMS 1.1.1)
                const srsNodes = layerNode.getElementsByTagName('SRS');
                for (let i = 0; i < srsNodes.length; i++) {
                    info.availableCRS.push(srsNodes[i].textContent);
                }
            } else {
                for (let i = 0; i < crsNodes.length; i++) {
                    info.availableCRS.push(crsNodes[i].textContent);
                }
            }

            // 默认使用第一个CRS
            if (info.availableCRS.length > 0) {
                info.srs = info.availableCRS[0];
            }

            // 解析地理边界
            const geoBBox = layerNode.getElementsByTagName('EX_GeographicBoundingBox')[0];
            if (geoBBox) {
                info.minX = parseFloat(this._getElementText(geoBBox, 'westBoundLongitude'));
                info.maxX = parseFloat(this._getElementText(geoBBox, 'eastBoundLongitude'));
                info.minY = parseFloat(this._getElementText(geoBBox, 'southBoundLatitude'));
                info.maxY = parseFloat(this._getElementText(geoBBox, 'northBoundLatitude'));
            } else {
                // 尝试LatLonBoundingBox (WMS 1.1.1)
                const llBBox = layerNode.getElementsByTagName('LatLonBoundingBox')[0];
                if (llBBox) {
                    info.minX = parseFloat(llBBox.getAttribute('minx'));
                    info.maxX = parseFloat(llBBox.getAttribute('maxx'));
                    info.minY = parseFloat(llBBox.getAttribute('miny'));
                    info.maxY = parseFloat(llBBox.getAttribute('maxy'));
                }
            }

            // 解析边界框
            if(!geoBBox){
                const bboxNodes = layerNode.getElementsByTagName('BoundingBox');
                if (bboxNodes.length > 0) {
                    // 使用与默认SRS匹配的边界框
                    for (let i = 0; i < bboxNodes.length; i++) {
                        const bbox = bboxNodes[i];
                        const crs = bbox.getAttribute('CRS') || bbox.getAttribute('SRS');
                        if (crs === info.srs) {
                            info.minX = parseFloat(bbox.getAttribute('minx'));
                            info.minY = parseFloat(bbox.getAttribute('miny'));
                            info.maxX = parseFloat(bbox.getAttribute('maxx'));
                            info.maxY = parseFloat(bbox.getAttribute('maxy'));
                            break;
                        }
                    }
                }
            }

            // 解析关键词
            const keywordList = layerNode.getElementsByTagName('KeywordList')[0];
            if (keywordList) {
                const keywords = keywordList.getElementsByTagName('Keyword');
                for (let i = 0; i < keywords.length; i++) {
                    info.keywords.push(keywords[i].textContent);
                }
            }

            // 解析样式
            const styleNodes = layerNode.getElementsByTagName('Style');
            for (let i = 0; i < styleNodes.length; i++) {
                const style = styleNodes[i];
                info.availableStyles.push({
                    name: this._getElementText(style, 'Name'),
                    title: this._getElementText(style, 'Title'),
                    abstract: this._getElementText(style, 'Abstract')
                });
            }

            // 设置默认样式为第一个样式
            if (info.availableStyles.length > 0) {
                info.style = info.availableStyles[0].name;
            }

            return info;
        }

        /**
         * 获取指定图层的WMSInformation对象
         * @param {String} layerName 图层名称
         * @param {Function} callback 回调函数，参数为(error, wmsInfo)
         */
        getLayerInformation(layerName, callback) {
            this.getLayerInfo(layerName, (error, layerInfo) => {
                if (error) {
                    if (defined(callback)) {
                        callback(error, null);
                    }
                    return;
                }

                if (defined(callback)) {
                    callback(null, layerInfo);
                }
            });
        }
    }

    return WMSConnector;
});